import clsx from 'clsx'
import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'

import { SigninRedirectArgs } from 'oidc-client-ts'
import {
  useAddToFavorites,
  useRemoveFromFavorites,
} from '../../hooks/apiHooks/MentorHooks'
import { useMentorsState } from '../../hooks/storeHooks/useMentors'
import useOnScreen from '../../hooks/useOnScreen'
import { MentorBase } from '../../types/mentor'
import { analytics } from '../../utils/analytics'
import { isTouchDevice } from '../../utils/browserDetect'
import { AnimatedSpinner } from '../AnimatedSpinner'
import { HaloLink } from '../HaloLink'
import { HeartIcon } from '../Icons'
import { Tooltip } from '../Tooltip'
import { TooltipHandle } from '../Tooltip/Tooltip'
const blackImage = '/images/black.jpeg'

interface MentorCardProps {
  profile: MentorBase
  renderCount: number
  isAuthenticated?: boolean
  isFavorite: boolean
  inFavorites?: boolean
  isProcessing?: boolean
  location: string
  removeMentor?: (mentorId: string) => void
  setInDeleteProcess?: (val: boolean) => void
  signinRedirect?: (options: SigninRedirectArgs) => void
  showHeadline?: boolean
}

export default function MentorCard({
  profile,
  renderCount,
  isAuthenticated,
  isFavorite,
  inFavorites,
  isProcessing: isProcessingProp,
  location,
  removeMentor,
  setInDeleteProcess,
  signinRedirect,
  showHeadline,
}: MentorCardProps) {
  const { mutateAsync: addToFavorites } = useAddToFavorites()
  const { mutateAsync: removeFromFavorites } = useRemoveFromFavorites()
  const imgRef = useRef<HTMLDivElement | null>(null)
  const iseOnScreen = useOnScreen(
    imgRef,
    [renderCount],
    `${window.innerHeight}px 0px`,
  )
  const { addToFavorites: addToStore, removeFromFavorites: removeFromStore } =
    useMentorsState()

  const tooltipRef = useRef<TooltipHandle>(null)

  const [isProcessing, setIsProcessing] = useState(false)

  const handleCreateAccount = (e: React.MouseEvent<HTMLDivElement>) => {
    if (signinRedirect) {
      e.stopPropagation()
      void signinRedirect({
        state: {
          returnUrl: `/${profile?.slug}?start=true`,
        },
        extraQueryParams: {
          action: 'get-started',
          backgroundUrl: profile?.videoLoopUrl || '',
        },
      })
    }
  }

  const handleFavoriteClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsProcessing(true)
    if (isFavorite) {
      if (setInDeleteProcess) setInDeleteProcess(true)
      analytics.logEvent('remove_from_favorites', {
        companion_slug: profile.slug,
      })
      removeFromFavorites(profile.mentorId)
        .then(() => {
          if (isTouchDevice()) {
            tooltipRef.current?.close()
            setTimeout(() => {
              if (inFavorites && removeMentor) {
                removeMentor(profile.mentorId)
              }
              removeFromStore(profile.mentorId)
            }, 200)
            toast.success('Companion removed from favorites')
            return
          }
          if (inFavorites && removeMentor) {
            removeMentor(profile.mentorId)
          }
          removeFromStore(profile.mentorId)
          toast.success('Companion removed from favorites')
        })
        .catch(() => {
          if (isTouchDevice()) {
            tooltipRef.current?.close()
          }
          toast.error('Failed to remove companion from favorites')
        })
        .finally(() => {
          if (!inFavorites) setIsProcessing(false)
        })
    } else {
      analytics.logEvent('add_to_favorites', {
        companion_slug: profile.slug,
      })
      addToFavorites(profile.mentorId)
        .then(() => {
          if (isTouchDevice()) {
            tooltipRef.current?.close()
            setTimeout(() => {
              addToStore(profile.mentorId)
            }, 200)
            toast.success('Companion added to favorites')
            return
          }
          addToStore(profile.mentorId)
          toast.success('Companion added to favorites')
        })
        .catch(() => {
          toast.error('Failed to add companion to favorites')
          if (isTouchDevice()) {
            tooltipRef.current?.close()
          }
        })
        .finally(() => setIsProcessing(false))
    }
  }

  return (
    <>
      <Tooltip
        content="Add to View"
        anchor={`#add-to-halo-${profile.mentorId}`}
        placement="top"
      />
      <Tooltip
        ref={tooltipRef}
        content={`${isFavorite ? 'Remove from favorites' : 'Add to favorites'}`}
        anchor={`#add-to-favorites-${profile.mentorId}`}
      />
      <div
        className="tc-mv-card-item"
        data-id={profile.mentorId}
        data-slug={profile.slug}
        data-testid="companion-card"
        ref={imgRef}
      >
        <video
          preload="metadata"
          src={
            iseOnScreen
              ? profile?.videoLoopUrl || profile?.videoIdleLoopUrl
              : undefined
          }
          muted
          autoPlay
          loop
          playsInline
          controlsList="nodownload"
          disablePictureInPicture
          poster={profile?.previewUrl || profile?.previewImageUrl || blackImage}
        />

        {isAuthenticated ? (
          <Link
            to={`/${profile?.slug}?start=true`}
            state={{ prevLocation: location }}
            className="absolute top-0 left-0 right-0 bottom-0 z-10"
            data-testid="start-session-link"
          />
        ) : (
          <div
            className="absolute top-0 left-0 right-0 bottom-0 z-10 cursor-pointer"
            onClick={handleCreateAccount}
          />
        )}

        {isAuthenticated && (
          <div className="absolute top-4 right-4 z-20 flex gap-2 tc-mv-card-controls">
            <HaloLink mentorId={profile.mentorId} showLink />

            <div
              className={`tc-mv-card-favorite-icon z-20 ${
                isFavorite || isProcessing ? 'active' : ''
              }`}
            >
              <button
                className={`color-white ${
                  isFavorite || isProcessing ? 'active' : ''
                }`}
                onClick={handleFavoriteClick}
                disabled={isProcessingProp}
                id={`add-to-favorites-${profile.mentorId}`}
                data-testid="favorite-button"
              >
                {isProcessing ? <AnimatedSpinner /> : <HeartIcon />}
              </button>
            </div>
          </div>
        )}

        <div className="tc-mv-avatar-details">
          <div
            title={
              profile.call_to_action && !showHeadline
                ? profile.call_to_action
                : profile.headline
            }
            className={clsx(
              'tc-mv-avatar-name mb-1 capitalize line-clamp-2 lg:max-w-[70%]',
              {
                'lg:max-w-[90%]': !profile.call_to_action || showHeadline,
              },
            )}
          >{`${profile.call_to_action && !showHeadline ? profile.call_to_action : profile.headline}`}</div>
          <div className="md:flex items-center gap-2 text-dark-neutral-400 whitespace-nowrap flex-wrap text-sm">
            <div className="inline-flex items-center gap-2">
              <span>{`${profile.firstName} ${profile.lastName}`}</span>
              <span
                className="w-1 h-1 bg-current rounded-full"
                aria-hidden="true"
              />
              <span>{profile.jobTitle}</span>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
